import React, { useCallback, useEffect, useRef, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Document, Page } from "react-pdf";
import * as pdfjsLib from "pdfjs-dist/webpack";
import { handleDynamicFontResize } from "../uploaderComponents/helpers/processFont";

import {
  Box,
  Button,
  Pagination,
  Slider,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  PanTool as HandIcon,
  Edit as TypeIcon,
} from "@mui/icons-material";

// Import the existing components and helpers
import FormFieldToolsComponent from "../uploaderComponents/FormFieldToolsComponent";
import { handleMouseDown } from "../uploaderComponents/helpers/mouseEvents.js/mouseDown";
import { handleMouseMove } from "../uploaderComponents/helpers/mouseEvents.js/mouseMove";
import { handleMouseUp } from "../uploaderComponents/helpers/mouseEvents.js/mouseUp";
import { handleKeyDown } from "../uploaderComponents/helpers/keyEvents.js/keyDown";
import { extractFormFields } from "../uploaderComponents/helpers/processPDF";

// put this near your component
const checkboxStyle = (isChecked) => {
  const crossSvg = encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
      stroke="#111" stroke-width="3" fill="none" stroke-linecap="round" stroke-linejoin="round">
      <line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/>
    </svg>`
  );
  return {
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    backgroundColor: "#fff",
    border: "1px solid #111",
    borderRadius: 2,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundSize: "80% 80%",
    ...(isChecked
      ? { backgroundImage: `url("data:image/svg+xml,${crossSvg}")` }
      : { backgroundImage: "none" }),
    // focus + hover (simple, no fancy color)
    outline: "none",
    boxShadow: "none",
  };
};

const PDFFormEditor = ({ formData, onClose, onSave, isOpen = false }) => {
  // State management
  const [currentProcessingFile, setCurrentProcessingFile] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [pdfSize, setPdfSize] = useState(800);
  const [actualWidth, setActualWidth] = useState(612);
  const [actualHeight, setActualHeight] = useState(792);
  const [pdfInput, setPdfInput] = useState(null); // File | base64 string
  const [base64File, setBase64File] = useState(null);

  // Mouse interaction states
  const [resizing, setResizing] = useState(false);
  const [moving, setMoving] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [rect, setRect] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [selectionRect, setSelectionRect] = useState(null);
  const [freeSelectStartPoint, setFreeSelectStartPoint] = useState(null);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [initialMousePosition, setInitialMousePosition] = useState({
    x: 0,
    y: 0,
  });
  const [initialInputSize, setInitialInputSize] = useState({
    width: 0,
    height: 0,
  });
  const [inputPosition, setInputPosition] = useState({ x: 0, y: 0 });
  const [currCursor, setCurrCursor] = useState("default");
  const [resizeDirection, setResizeDirection] = useState("");

  // Tool states
  const [selectToolActive, setSelectToolActive] = useState(true);
  const [typingToolActive, setTypingToolActive] = useState(false);
  const [expandToolBox, setExpandToolBox] = useState(true);

  const [reverseDeleted, setReverseDeleted] = useState([]);
  const [focusedField, setFocusedField] = useState(null);
  const [highlightFields, setHighlightFields] = useState(true);

  // Refs
  const pdfContainerRef = useRef(null);
  const pdfDocumentRef = useRef(null);
  const extractedOnceRef = useRef(false);
  const inputRef = useRef(null);
  const childInputRef = useRef(null);

  const fixedInputs = [
    "notary_seal_a",
    "notary_sign_a",
    "notary_seal_b",
    "notary_sign_b",
    "sign_a",
    "sign_b",
    "sign_c",
  ];

  const base64ToUint8Array = (base64) => {
    const binaryString = atob(base64.split(",")[1]);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  };

  const arrayBufferToBase64DataUrl = (buf) => {
    const bytes = new Uint8Array(buf);
    let binary = "";
    const chunk = 0x8000; // 32KB
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
    }
    return "data:application/pdf;base64," + btoa(binary);
  };

  const hasPDFHeader = (uint8Array) => {
    const header = new TextDecoder().decode(uint8Array.slice(0, 5));
    return header === "%PDF-";
  };

  // Initialize the processing file structure when formData changes
  useEffect(() => {
    if (!formData || !isOpen) return;

    const processingFile = {
      document: {
        tempFields: {
          currentDocument: {
            base64File: formData.base64File || null,
            pdfSize: pdfSize,
            actualWidth: actualWidth,
            actualHeight: actualHeight,
            scaleFactor: pdfSize / (actualWidth || 1),
          },
          formFields: formData.formFields || [],
          selectedFormField: null,
          selectedExtraFields: [],
          formFieldTools: {
            textField: { active: false, name: "textField" },
            checkbox: { active: false, name: "checkbox" },
            radio: { active: false, name: "radio" },
            date: { active: false, name: "date" },
            notarySeal_a: {
              active: false,
              name: "notary_seal_a",
              disabled: false,
            },
            notarySignature_a: {
              active: false,
              name: "notary_sign_a",
              disabled: false,
            },
            notarySeal_b: {
              active: false,
              name: "notary_seal_b",
              disabled: false,
            },
            notarySignature_b: {
              active: false,
              name: "notary_sign_b",
              disabled: false,
            },
            signatureA: { active: false, name: "sign_a", disabled: false },
            signatureB: { active: false, name: "sign_b", disabled: false },
            signatureC: { active: false, name: "sign_c", disabled: false },
          },
        },
      },
    };
    setCurrentProcessingFile(processingFile);

    const hasFile = !!formData.pdfFile;
    const hasBase64 = !!formData.base64File;
    const hasUrl = !!formData.pdfUrl;

    (async () => {
      if (hasFile) {
        await validateAndProcessFile(formData.pdfFile);
      } else if (hasBase64) {
        setBase64File(formData.base64File);
        setPdfInput(formData.base64File);
      } else if (hasUrl) {
        const res = await fetch(formData.pdfUrl, { credentials: "include" });
        if (!res.ok) throw new Error(`Failed to fetch PDF: ${res.status}`);
        const buf = await res.arrayBuffer();
        const b64 = arrayBufferToBase64DataUrl(buf);
        setBase64File(b64);
        setPdfInput(b64);
        // Extract once here so downstream effect doesn't race:
        const fields = await extractFormFields(b64);
        setCurrentProcessingFile((prev) => ({
          ...prev,
          document: {
            ...prev.document,
            tempFields: {
              ...prev.document.tempFields,
              formFields: fields || [],
              currentDocument: {
                ...prev.document.tempFields.currentDocument,
                base64File: b64,
              },
            },
          },
        }));
        extractedOnceRef.current = true; // prevent the other extractor from overwriting later
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, isOpen]);

  //   Event listeners
  useEffect(() => {
    const handleWheel = (e) => {
      if (
        !currentProcessingFile?.document?.tempFields?.currentDocument.base64File
      )
        return;
      if (e.ctrlKey) {
        e.preventDefault(); // Prevent default zooming behavior

        const currentScale =
          currentProcessingFile?.document?.tempFields?.currentDocument
            ?.scaleFactor || scale;
        const newScale =
          e.deltaY < 0
            ? Math.min(currentScale + 0.1, 2.0) // Zoom in
            : Math.max(currentScale - 0.1, 0.5); // Zoom out

        // Update the local scale state (this is what the PDF component uses)
        setScale(newScale);

        // Update the currentProcessingFile state
        setCurrentProcessingFile((prevFile) => ({
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevFile.document.tempFields,
              currentDocument: {
                ...prevFile.document.tempFields.currentDocument,
                pdfSize: newScale * actualWidth,
                scaleFactor: newScale,
              },
            },
          },
        }));
      }
    };

    document.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      document.removeEventListener("wheel", handleWheel);
    };
  }, [
    currentProcessingFile?.document?.tempFields?.currentDocument?.base64File,
    actualWidth,
    scale,
    currentProcessingFile?.document?.tempFields?.currentDocument?.scaleFactor,
  ]);

  useEffect(() => {
    const handleKeyDownWrapper = (e) => {
      handleKeyDown(
        e,
        currentProcessingFile,
        setCurrentProcessingFile,
        typingToolActive,
        setReverseDeleted,
        setCurrCursor,
        selectToolActive,
        focusedField,
        setTypingToolActive,
        setSelectToolActive,
        currCursor,
        setFocusedField,
        setHighlightFields,
        expandToolBox,
        setExpandToolBox
      );
    };
    // Attach mousemove and mouseup events to the document
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("keydown", handleKeyDownWrapper);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("keydown", handleKeyDownWrapper);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    resizing,
    moving,
    isDrawing,
    isSelecting,
    currentProcessingFile?.document?.tempFields?.selectedFormField,
    typingToolActive,
    focusedField,
    selectToolActive,
    currentProcessingFile?.document?.tempFields?.formFieldTools,
    reverseDeleted,
  ]);

  // Attach the event listener to the document
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (resizing) return;
      // Check if the click is inside the PDF viewer container
      const isInsidePdfViewer =
        pdfContainerRef.current &&
        pdfContainerRef.current.contains(event.target);

      // Check if the click is inside the input field
      const isInsideInputField =
        inputRef.current && inputRef.current.contains(event.target);

      const isInsideChildInputField =
        childInputRef.current && childInputRef.current.contains(event.target);

      // If the click is inside the PDF viewer but outside of the input field, clear the selected form field
      if (
        isInsidePdfViewer &&
        !isInsideInputField &&
        !isInsideChildInputField
      ) {
        setCurrentProcessingFile((prevFile) => ({
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevFile.document.tempFields,
              selectedFormField: null,
              selectedExtraFields: [],
            },
          },
        }));
      }
    };

    document.addEventListener("dblclick", handleClickOutside);

    return () => {
      document.removeEventListener("dblclick", handleClickOutside);
    };
  }, [resizing]);

  const handleFieldChange = (e, field) => {
    const newValue = e.target.value;

    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFields: prevFile.document.tempFields.formFields.map((f) => {
            if (f.name !== field.name) return f;

            // If autoFontSize is false, only update the value
            if (!f.autoFontSize) {
              return { ...f, value: newValue };
            }

            const { newFontSize, fontSizes } = handleDynamicFontResize(
              field.width,
              field.height,
              f.fontSize,
              newValue,
              f.fontSizes || [],
              f.value
            );

            return {
              ...f,
              value: newValue,
              fontSize: newFontSize, // Apply correct converted font size
              fontSizes: fontSizes, // Store history in correct format
            };
          }),
        },
      },
    }));
  };
  //!
  //   // If we receive a File, convert to base64 data URL like UploadBox does
  const validateAndProcessFile = async (file) => {
    if (file && file.type === "application/pdf") {
      const reader = new FileReader();
      try {
        reader.onload = async function () {
          const base64String = reader.result;
          const uint8Data = base64ToUint8Array(base64String);

          if (!hasPDFHeader(uint8Data)) {
            console.error("Invalid PDF header");
            return;
          }
          try {
            await pdfjsLib.getDocument({ data: uint8Data }).promise;
            const extractedFields = await extractFormFields(base64String);
            setBase64File(base64String);
            setCurrentProcessingFile((prevFile) => ({
              ...prevFile,
              document: {
                ...prevFile.document,
                tempFields: {
                  ...prevFile.document.tempFields,
                  formFields: extractedFields,
                  currentDocument: {
                    ...prevFile.document.tempFields.currentDocument,
                    base64File: base64String,
                    files: "",
                  },
                },
              },
            }));
            extractedOnceRef.current = true;
          } catch (e) {
            console.error("Error loading PDF:", e);
          }
        };

        reader.readAsDataURL(file);
      } catch (e) {
        console.error("Error loading PDF:", e);
      }
    }
  };

  // Keep scaleFactor in sync with pdfSize and actualWidth
  useEffect(() => {
    setCurrentProcessingFile((prev) => {
      if (!prev) return prev;
      const aw =
        prev?.document?.tempFields?.currentDocument?.actualWidth ||
        actualWidth ||
        1;
      const newScale =
        (prev?.document?.tempFields?.currentDocument?.pdfSize || pdfSize) / aw;
      return {
        ...prev,
        document: {
          ...prev.document,
          tempFields: {
            ...prev.document.tempFields,
            currentDocument: {
              ...prev.document.tempFields.currentDocument,
              scaleFactor: newScale,
            },
          },
        },
      };
    });
  }, [pdfSize, actualWidth]);

  // Reset extraction guard when editor closes
  useEffect(() => {
    if (!isOpen) extractedOnceRef.current = false;
  }, [isOpen]);

  // Handle PDF load
  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
  }, []);

  // Handle page load
  const onPageLoadSuccess = useCallback(
    (page) => {
      const { width, height } = page.originalWidth
        ? { width: page.originalWidth, height: page.originalHeight }
        : { width: 612, height: 792 };

      setActualWidth(width);
      setActualHeight(height);

      // Update the current document dimensions and ensure proper scaleFactor
      setPdfSize(scale * width);

      setCurrentProcessingFile((prev) => ({
        ...prev,
        document: {
          ...prev.document,
          tempFields: {
            ...prev.document.tempFields,
            currentDocument: {
              ...prev.document.tempFields.currentDocument,
              actualWidth: width,
              actualHeight: height,
              pdfSize: scale * width,
              scaleFactor: scale,
              pageNumber: pageNumber,
            },
          },
        },
      }));
    },
    [scale, pageNumber]
  );

  // Handle scale change
  const handleScaleChange = useCallback(
    (event, newValue) => {
      setScale(newValue);
      setPdfSize(newValue * actualWidth);

      setCurrentProcessingFile((prev) => ({
        ...prev,
        document: {
          ...prev.document,
          tempFields: {
            ...prev.document.tempFields,
            currentDocument: {
              ...prev.document.tempFields.currentDocument,
              pdfSize: newValue * actualWidth,
              scaleFactor: newValue,
            },
          },
        },
      }));
    },
    [actualWidth]
  );

  // Handle page change
  const handlePageChange = useCallback((event, value) => {
    setPageNumber(value);
    setCurrentProcessingFile((prev) =>
      prev
        ? {
            ...prev,
            document: {
              ...prev.document,
              tempFields: {
                ...prev.document.tempFields,
                currentDocument: {
                  ...prev.document.tempFields.currentDocument,
                  pageNumber: value,
                },
              },
            },
          }
        : prev
    );
  }, []);

  // Tool toggle handlers
  const handleToolToggle = (event, newTool) => {
    if (newTool === null) return;

    if (newTool === "hand") {
      setCurrCursor("default");
      setSelectToolActive(true);
      setTypingToolActive(false);
      setCurrentProcessingFile((prevFile) => ({
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            selectedFormField: null,
            selectedExtraFields: [],
            formFieldTools: Object.keys(
              prevFile.document.tempFields.formFieldTools
            ).reduce((acc, key) => {
              acc[key] = {
                ...prevFile.document.tempFields.formFieldTools[key],
                active: false,
                values: [],
              };
              return acc;
            }, {}),
          },
        },
      }));
    } else if (newTool === "type") {
      setCurrCursor("default");
      setSelectToolActive(false);
      setTypingToolActive(true);
      setCurrentProcessingFile((prevFile) => ({
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            selectedFormField: null,
            selectedExtraFields: [],
          },
        },
      }));
    }
  };

  // Handle keyboard events
  const handleKeyDownEvent = useCallback(
    (e) => {
      if (!currentProcessingFile) return;

      handleKeyDown(
        e,
        currentProcessingFile,
        setCurrentProcessingFile,
        typingToolActive,
        setReverseDeleted,
        setCurrCursor,
        selectToolActive,
        focusedField,
        setTypingToolActive,
        setSelectToolActive,
        currCursor,
        setFocusedField,
        setHighlightFields,
        expandToolBox,
        setExpandToolBox
      );
    },
    [
      currentProcessingFile,
      typingToolActive,
      selectToolActive,
      focusedField,
      currCursor,
      expandToolBox,
    ]
  );

  // Handle save
  const handleSave = useCallback(() => {
    if (onSave && currentProcessingFile) {
      const formFields = currentProcessingFile.document.tempFields.formFields;
      onSave({
        ...formData,
        formFields: formFields,
      });
    }
  }, [onSave, currentProcessingFile, formData]);

  // Handle close
  const handleClose = useCallback(() => {
    // Reset to page 1 when closing
    setPageNumber(1);
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  if (!isOpen || !formData) {
    return null;
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
        }}
        onKeyDown={handleKeyDownEvent}
        tabIndex={0}
      >
        {/* Header */}
        <Box
          sx={{
            backgroundColor: "white",
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="h6">
            Edit Form: {formData.formName || formData.fileName}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="contained" onClick={handleSave}>
              Save Changes
            </Button>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
          {/* Left Sidebar - Tools */}
          <Box
            sx={{
              width: 300,
              backgroundColor: "white",
              borderRight: "1px solid #e0e0e0",
              overflowY: "auto",
              p: 2,
            }}
          >
            <FormFieldToolsComponent
              currentProcessingFile={currentProcessingFile}
              setCurrentProcessingFile={setCurrentProcessingFile}
              expandToolBox={expandToolBox}
              setExpandToolBox={setExpandToolBox}
              setSelectToolActive={setSelectToolActive}
              typingToolActive={typingToolActive}
              setTypingToolActive={setTypingToolActive}
              setCurrCursor={setCurrCursor}
              fixedInputs={[]}
            />
          </Box>

          {/* Center - PDF Viewer */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#f5f5f5",
              overflow: "hidden",
            }}
          >
            {/* PDF Controls */}
            <Box
              sx={{
                backgroundColor: "white",
                p: 2,
                borderBottom: "1px solid #e0e0e0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {/* Tool Toggle */}
                <ToggleButtonGroup
                  value={selectToolActive ? "hand" : "type"}
                  exclusive
                  onChange={handleToolToggle}
                  size="small"
                >
                  <ToggleButton value="hand">
                    <Tooltip title="Hand Tool - Select and Move Fields">
                      <HandIcon />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="type">
                    <Tooltip title="Type Tool - Edit Field Content">
                      <TypeIcon />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>

                <Typography variant="body2">Zoom:</Typography>
                <Slider
                  value={scale}
                  onChange={handleScaleChange}
                  min={0.5}
                  max={2.0}
                  step={0.1}
                  sx={{ width: 100 }}
                />
                <Typography variant="body2">
                  {Math.round(scale * 100)}%
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={highlightFields}
                      onChange={(e) => setHighlightFields(e.target.checked)}
                      color="warning"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2">Show highlights</Typography>
                  }
                  sx={{ ml: 2 }}
                />
              </Box>

              {numPages > 1 && (
                <Pagination
                  count={numPages}
                  page={pageNumber}
                  onChange={handlePageChange}
                  size="small"
                />
              )}
            </Box>

            {/* PDF Viewer */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start", // Align to top to ensure full upward scroll when zoomed
                overflow: "auto",
                p: 2,
              }}
            >
              {(base64File || pdfInput) && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "rgba(228, 228, 228, 0.5)",
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Box
                    ref={pdfContainerRef}
                    sx={{
                      position: "relative",
                      display: "inline-block",
                      border: "2px solid #d3d3d3",
                      borderRadius: "18px",
                      boxShadow: "3px 4px 12px rgba(0, 0, 0, 0.3)",
                      backgroundColor: "#fff",
                      cursor: selectToolActive ? currCursor : "default",
                      userSelect: "none",
                      minWidth: "612px",
                      minHeight: "792px",
                      width: "auto",
                      height: "auto",
                    }}
                    onMouseDown={(e) => {
                      const isToolActive = Object.values(
                        currentProcessingFile?.document?.tempFields
                          ?.formFieldTools
                      ).some((field) => field.active);

                      if (isToolActive) {
                        handleMouseDown(
                          e,
                          currentProcessingFile,
                          setCurrentProcessingFile,
                          null,
                          null,
                          "fieldTools",
                          "crosshair",
                          pdfContainerRef,
                          setStartPoint,
                          setRect,
                          setIsDrawing,
                          setCurrCursor,
                          setFreeSelectStartPoint,
                          setMoving,
                          setIsSelecting,
                          setInitialMousePosition,
                          setInitialInputSize,
                          setInputPosition,
                          setSelectionRect,
                          setResizing,
                          setResizeDirection
                        );
                      } else if (selectToolActive && !isToolActive) {
                        handleMouseDown(
                          e,
                          currentProcessingFile,
                          setCurrentProcessingFile,
                          null,
                          null,
                          "freeSelect",
                          "default",
                          pdfContainerRef,
                          setStartPoint,
                          setRect,
                          setIsDrawing,
                          setCurrCursor,
                          setFreeSelectStartPoint,
                          setMoving,
                          setIsSelecting,
                          setInitialMousePosition,
                          setInitialInputSize,
                          setInputPosition,
                          setSelectionRect,
                          setResizing,
                          setResizeDirection
                        );
                      } else return;
                    }}
                    onMouseMove={(e) => {
                      const isToolActive = Object.values(
                        currentProcessingFile?.document?.tempFields
                          ?.formFieldTools
                      ).some((field) => field.active);
                      if (isToolActive) {
                        handleMouseMove(
                          e,
                          currentProcessingFile,
                          setCurrentProcessingFile,
                          pdfContainerRef,
                          resizing,
                          initialMousePosition,
                          initialInputSize,
                          inputPosition,
                          resizeDirection,
                          moving,
                          isDrawing,
                          startPoint,
                          setRect,
                          isSelecting,
                          freeSelectStartPoint,
                          setSelectionRect
                        );
                      } else if (selectToolActive && !isToolActive) {
                        handleMouseMove(
                          e,
                          currentProcessingFile,
                          setCurrentProcessingFile,
                          pdfContainerRef,
                          resizing,
                          initialMousePosition,
                          initialInputSize,
                          inputPosition,
                          resizeDirection,
                          moving,
                          isDrawing,
                          startPoint,
                          setRect,
                          isSelecting,
                          freeSelectStartPoint,
                          setSelectionRect
                        );
                      } else return;
                    }}
                    onMouseUp={() => {
                      const isToolActive = Object.values(
                        currentProcessingFile?.document?.tempFields
                          ?.formFieldTools
                      ).some((field) => field.active);

                      if (isToolActive) {
                        handleMouseUp(
                          currentProcessingFile,
                          setCurrentProcessingFile,
                          resizing,
                          setResizing,
                          setCurrCursor,
                          moving,
                          setMoving,
                          isDrawing,
                          rect,
                          setIsDrawing,
                          setRect,
                          fixedInputs,
                          isSelecting,
                          setIsSelecting,
                          setSelectionRect,
                          setFreeSelectStartPoint
                        );
                      } else if (selectToolActive && !isToolActive) {
                        handleMouseUp(
                          currentProcessingFile,
                          setCurrentProcessingFile,
                          resizing,
                          setResizing,
                          setCurrCursor,
                          moving,
                          setMoving,
                          isDrawing,
                          rect,
                          setIsDrawing,
                          setRect,
                          fixedInputs,
                          isSelecting,
                          setIsSelecting,
                          setSelectionRect,
                          setFreeSelectStartPoint
                        );
                      } else return;
                    }}
                  >
                    <Document
                      key={base64File ? "loaded" : "loading"}
                      file={base64File || pdfInput}
                      onLoadSuccess={(e) => {
                        onDocumentLoadSuccess(e);
                      }}
                      onLoadError={(e) => {
                        console.error("Document load error:", e);
                      }}
                      ref={pdfDocumentRef}
                    >
                      <Page
                        pageNumber={pageNumber}
                        scale={scale}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                        style={{ border: "2px solid red" }}
                        onLoadSuccess={(page) => {
                          onPageLoadSuccess(page);
                        }}
                        onLoadError={(error) => {
                          console.error("Page load error:", error);
                        }}
                      />
                    </Document>

                    {isDrawing && (
                      <Box
                        sx={{
                          position: "absolute",
                          left: `${rect.x * (currentProcessingFile?.document?.tempFields?.currentDocument?.scaleFactor || 1)}px`,
                          top: `${rect.y * (currentProcessingFile?.document?.tempFields?.currentDocument?.scaleFactor || 1)}px`,
                          width: `${rect.width * (currentProcessingFile?.document?.tempFields?.currentDocument?.scaleFactor || 1)}px`,
                          height: `${rect.height * (currentProcessingFile?.document?.tempFields?.currentDocument?.scaleFactor || 1)}px`,
                          border: "2px dotted blue",
                        }}
                      />
                    )}

                    {isSelecting && selectionRect && (
                      <Box
                        sx={{
                          position: "absolute",
                          left: `${selectionRect.x * (currentProcessingFile?.document?.tempFields?.currentDocument?.scaleFactor || 1)}px`,
                          top: `${selectionRect.y * (currentProcessingFile?.document?.tempFields?.currentDocument?.scaleFactor || 1)}px`,
                          width: `${selectionRect.width * (currentProcessingFile?.document?.tempFields?.currentDocument?.scaleFactor || 1)}px`,
                          height: `${selectionRect.height * (currentProcessingFile?.document?.tempFields?.currentDocument?.scaleFactor || 1)}px`,
                          border: "2px dotted blue",
                        }}
                      />
                    )}

                    {currentProcessingFile?.document?.tempFields?.formFieldTools.textField.values?.map(
                      (field, index) => (
                        <Box
                          key={index}
                          sx={{
                            position: "absolute",
                            left: `${field.x}px`,
                            top: `${field.y}px`,
                            width: `${field.width}px`,
                            height: `${field.height}px`,
                            border: "1px solid #000",
                          }}
                        />
                      )
                    )}

                    {/* Render form fields overlay (filter by current page) */}
                    {currentProcessingFile?.document?.tempFields?.formFields
                      ?.filter((field) =>
                        typeof field.page === "number"
                          ? field.page === pageNumber
                          : true
                      )
                      .map((field, index) => {
                        const scaleFactor = scale;

                        return (
                          <React.Fragment key={index}>
                            {currentProcessingFile?.document?.tempFields
                              ?.selectedFormField &&
                              currentProcessingFile?.document?.tempFields
                                ?.selectedFormField?.name === field?.name &&
                              selectToolActive && (
                                <Box
                                  ref={inputRef}
                                  sx={{
                                    position: "absolute",
                                    left:
                                      field.type === "PDFCheckBox" ||
                                      field.type === "PDFRadioGroup"
                                        ? `${field.x * scaleFactor + 2}px`
                                        : `${field.x * scaleFactor}px`,
                                    top:
                                      field.type === "PDFCheckBox" ||
                                      field.type === "PDFRadioGroup"
                                        ? `${
                                            (currentProcessingFile?.document
                                              ?.tempFields?.currentDocument
                                              .actualHeight -
                                              field.y -
                                              field.height) *
                                              scaleFactor +
                                            1
                                          }px`
                                        : `${
                                            (currentProcessingFile?.document
                                              ?.tempFields?.currentDocument
                                              .actualHeight -
                                              field.y -
                                              field.height) *
                                            scaleFactor
                                          }px`,
                                    width:
                                      currentProcessingFile?.document
                                        ?.tempFields?.selectedFormField &&
                                      field.name ===
                                        currentProcessingFile?.document
                                          ?.tempFields?.selectedFormField.name
                                        ? `${
                                            currentProcessingFile?.document
                                              ?.tempFields?.selectedFormField
                                              .width * scaleFactor
                                          }px`
                                        : `${field.width * scaleFactor}px`,
                                    height:
                                      currentProcessingFile?.document
                                        ?.tempFields?.selectedFormField &&
                                      field.name ===
                                        currentProcessingFile?.document
                                          ?.tempFields?.selectedFormField.name
                                        ? `${
                                            currentProcessingFile?.document
                                              ?.tempFields?.selectedFormField
                                              .height * scaleFactor
                                          }px`
                                        : `${field.height * scaleFactor}px`,
                                    border: "2px dotted blue",
                                    backgroundColor: "transparent",
                                    zIndex: 100,
                                  }}
                                  onMouseDown={(e) =>
                                    handleMouseDown(
                                      e,
                                      currentProcessingFile,
                                      setCurrentProcessingFile,
                                      field,
                                      scaleFactor,
                                      "move",
                                      "default",
                                      pdfContainerRef,
                                      setStartPoint,
                                      setRect,
                                      setIsDrawing,
                                      setCurrCursor,
                                      setFreeSelectStartPoint,
                                      setMoving,
                                      setIsSelecting,
                                      setInitialMousePosition,
                                      setInitialInputSize,
                                      setInputPosition,
                                      setSelectionRect,
                                      setResizing,
                                      setResizeDirection
                                    )
                                  }
                                  onDoubleClick={() => {
                                    if (
                                      currentProcessingFile?.document
                                        ?.tempFields?.selectedExtraFields
                                        .length > 1
                                    ) {
                                      setCurrentProcessingFile((prevFile) => ({
                                        ...prevFile,
                                        document: {
                                          ...prevFile.document,
                                          tempFields: {
                                            ...prevFile.document.tempFields,
                                            selectedExtraFields:
                                              prevFile.document.tempFields.selectedExtraFields.filter(
                                                (f) => f.name !== field.name
                                              ),
                                          },
                                        },
                                      }));
                                    }
                                  }}
                                >
                                  {/* Small Squares at Corners and Midpoints */}
                                  {[
                                    {
                                      top: 0,
                                      left: -1,
                                      cursor: "nwse-resize",
                                      direction: "nw",
                                    }, // Top Left
                                    {
                                      top: -1,
                                      left: "50%",
                                      cursor: "ns-resize",
                                      direction: "n",
                                    }, // Top Middle
                                    {
                                      top: 0,
                                      right: -12,
                                      cursor: "nesw-resize",
                                      direction: "ne",
                                    }, // Top Right
                                    {
                                      bottom: -11,
                                      left: -1,
                                      cursor: "nesw-resize",
                                      direction: "sw",
                                    }, // Bottom Left
                                    {
                                      bottom: -11,
                                      left: "50%",
                                      cursor: "ns-resize",
                                      direction: "s",
                                    }, // Bottom Middle
                                    {
                                      bottom: -11,
                                      right: -12,
                                      cursor: "nwse-resize",
                                      direction: "se",
                                    }, // Bottom Right
                                    {
                                      top: "50%",
                                      left: -1,
                                      cursor: "ew-resize",
                                      direction: "w",
                                    }, // Middle Left
                                    {
                                      top: "50%",
                                      right: -12,
                                      cursor: "ew-resize",
                                      direction: "e",
                                    }, // Middle Right
                                  ].map((pos, idx) => (
                                    <Box
                                      key={idx}
                                      sx={{
                                        position: "absolute",
                                        width: "8px", // Size of small squares
                                        height: "8px",
                                        border: "1px solid blue",
                                        borderRadius: "2px",
                                        transform: "translate(-50%, -50%)", // Center the squares
                                        ...pos, // Apply position dynamically
                                      }}
                                      onMouseDown={(e) => {
                                        e.stopPropagation();
                                        handleMouseDown(
                                          e,
                                          currentProcessingFile,
                                          setCurrentProcessingFile,
                                          field,
                                          scaleFactor,
                                          pos.direction,
                                          pos.cursor,
                                          pdfContainerRef,
                                          setStartPoint,
                                          setRect,
                                          setIsDrawing,
                                          setCurrCursor,
                                          setFreeSelectStartPoint,
                                          setMoving,
                                          setIsSelecting,
                                          setInitialMousePosition,
                                          setInitialInputSize,
                                          setInputPosition,
                                          setSelectionRect,
                                          setResizing,
                                          setResizeDirection
                                        );
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}
                            {currentProcessingFile?.document?.tempFields
                              ?.selectedExtraFields.length > 0 &&
                              currentProcessingFile?.document?.tempFields?.selectedExtraFields.some(
                                (f) =>
                                  f.name === field.name &&
                                  (!currentProcessingFile?.document?.tempFields
                                    ?.selectedFormField ||
                                    currentProcessingFile?.document?.tempFields
                                      ?.selectedFormField?.name !== f.name)
                              ) && (
                                <Box
                                  ref={childInputRef}
                                  onClick={(e) => {
                                    if (currCursor === "crosshair") return;
                                    if (e.ctrlKey || e.metaKey) {
                                      setCurrentProcessingFile((prevFile) => ({
                                        ...prevFile,
                                        document: {
                                          ...prevFile.document,
                                          tempFields: {
                                            ...prevFile.document.tempFields,
                                            selectedExtraFields:
                                              prevFile.document.tempFields.selectedExtraFields.filter(
                                                (f) => f.name !== field?.name
                                              ),
                                          },
                                        },
                                      }));
                                    } else {
                                      setCurrentProcessingFile((prevFile) => ({
                                        ...prevFile,
                                        document: {
                                          ...prevFile.document,
                                          tempFields: {
                                            ...prevFile.document.tempFields,
                                            selectedFormField: field,
                                          },
                                        },
                                      }));
                                    }
                                  }}
                                  sx={{
                                    position: "absolute",
                                    left:
                                      field.type === "PDFCheckBox" ||
                                      field.type === "PDFRadioGroup"
                                        ? `${field.x * scaleFactor + 2}px`
                                        : `${field.x * scaleFactor}px`,
                                    top:
                                      field.type === "PDFCheckBox" ||
                                      field.type === "PDFRadioGroup"
                                        ? `${
                                            (currentProcessingFile?.document
                                              ?.tempFields?.currentDocument
                                              .actualHeight -
                                              field.y -
                                              field.height) *
                                              scaleFactor +
                                            1
                                          }px`
                                        : `${
                                            (currentProcessingFile?.document
                                              ?.tempFields?.currentDocument
                                              .actualHeight -
                                              field.y -
                                              field.height) *
                                            scaleFactor
                                          }px`,
                                    width: `${field.width * scaleFactor}px`,
                                    height: `${field.height * scaleFactor}px`,
                                    border: "2px dotted blue",
                                    backgroundColor: "transparent",
                                    zIndex: 20,
                                  }}
                                ></Box>
                              )}
                            {field.type === "PDFCheckBox" ||
                            field.type === "PDFRadioGroup" ? (
                              <Box
                                sx={{
                                  position: "absolute",
                                  left: `${field.x * scaleFactor + 5}px`,
                                  top: `${
                                    (currentProcessingFile?.document?.tempFields
                                      ?.currentDocument.actualHeight -
                                      field.y -
                                      field.height) *
                                      scaleFactor +
                                    3
                                  }px`,
                                  width: `${field.width * scaleFactor}px`,
                                  height: `${field.height * scaleFactor}px`,

                                  backgroundColor:
                                    highlightFields ||
                                    focusedField === field.name
                                      ? "RGBA(255, 211, 0, 0.3)"
                                      : "transparent",
                                  zIndex: 20,
                                  cursor: typingToolActive
                                    ? "pointer"
                                    : "default",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (currCursor === "crosshair") return;

                                  if (typingToolActive) {
                                    setCurrentProcessingFile((prevFile) => ({
                                      ...prevFile,
                                      document: {
                                        ...prevFile.document,
                                        tempFields: {
                                          ...prevFile.document.tempFields,
                                          formFields:
                                            prevFile.document.tempFields.formFields.map(
                                              (f) =>
                                                f.name === field.name
                                                  ? {
                                                      ...f,
                                                      value: !field?.value,
                                                    }
                                                  : f // Clear checkbox value
                                            ),
                                        },
                                      },
                                    }));
                                  }
                                  if (
                                    currentProcessingFile?.document?.tempFields
                                      ?.selectedExtraFields.length > 0 &&
                                    currentProcessingFile?.document?.tempFields?.selectedExtraFields.some(
                                      (f) =>
                                        f.name === field.name &&
                                        (!currentProcessingFile?.document
                                          ?.tempFields?.selectedFormField ||
                                          currentProcessingFile?.document
                                            ?.tempFields?.selectedFormField
                                            ?.name !== f.name)
                                    )
                                  ) {
                                    if (e.ctrlKey || e.metaKey) {
                                      setCurrentProcessingFile((prevFile) => ({
                                        ...prevFile,
                                        document: {
                                          ...prevFile.document,
                                          tempFields: {
                                            ...prevFile.document.tempFields,
                                            selectedExtraFields:
                                              prevFile.document.tempFields.selectedExtraFields.filter(
                                                (f) => f.name !== field?.name
                                              ),
                                          },
                                        },
                                      }));
                                    } else {
                                      setCurrentProcessingFile((prevFile) => ({
                                        ...prevFile,
                                        document: {
                                          ...prevFile.document,
                                          tempFields: {
                                            ...prevFile.document.tempFields,
                                            selectedFormField: field,
                                          },
                                        },
                                      }));
                                    }
                                  } else {
                                    setCurrentProcessingFile((prevFile) => {
                                      const isAlreadySelected =
                                        prevFile.document.tempFields.selectedExtraFields.some(
                                          (f) => f.name === field.name
                                        );

                                      return {
                                        ...prevFile,
                                        document: {
                                          ...prevFile.document,
                                          tempFields: {
                                            ...prevFile.document.tempFields,
                                            selectedExtraFields:
                                              isAlreadySelected
                                                ? prevFile.document.tempFields
                                                    .selectedExtraFields
                                                : e.ctrlKey || e.metaKey
                                                  ? [
                                                      ...prevFile.document
                                                        .tempFields
                                                        .selectedExtraFields,
                                                      field,
                                                    ]
                                                  : [field],

                                            selectedFormField: { ...field },
                                          },
                                        },
                                      };
                                    });
                                  }
                                }}
                              ></Box>
                            ) : (
                              <></>
                            )}

                            <input
                              type={
                                field.type === "PDFCheckBox"
                                  ? "checkbox"
                                  : field.type === "PDFRadioGroup"
                                    ? "radio"
                                    : field.name.startsWith("date_field")
                                      ? "date"
                                      : "text"
                              }
                              value={
                                field.type === "PDFCheckBox" ||
                                field.type === "PDFRadioGroup"
                                  ? undefined
                                  : field.value || ""
                              }
                              checked={
                                field.type === "PDFCheckBox" ||
                                field.type === "PDFRadioGroup"
                                  ? field.value
                                  : undefined
                              }
                              onFocus={(e) => {
                                if (
                                  selectToolActive ||
                                  fixedInputs.some((prefix) =>
                                    field.name.startsWith(prefix)
                                  )
                                ) {
                                  e.preventDefault();
                                  return;
                                } else {
                                  setFocusedField(field.name);
                                }
                              }}
                              onMouseDown={(e) => {
                                if (
                                  (selectToolActive &&
                                    field.name.startsWith("date_field")) ||
                                  fixedInputs.some((prefix) =>
                                    field.name.startsWith(prefix)
                                  )
                                ) {
                                  e.preventDefault(); // Prevent selecting "mm/dd/yyyy"
                                }
                              }}
                              onBlur={() => setFocusedField(null)}
                              readOnly={
                                selectToolActive ||
                                fixedInputs.some((prefix) =>
                                  field.name.startsWith(prefix)
                                )
                              }
                              onChange={(e) => {
                                if (
                                  fixedInputs.some((prefix) =>
                                    field.name.startsWith(prefix)
                                  )
                                ) {
                                  return;
                                }
                                if (
                                  field.type === "PDFCheckBox" ||
                                  field.type === "PDFRadioGroup"
                                ) {
                                  if (selectToolActive) return;
                                  const isChecked = e.target.checked;
                                  setCurrentProcessingFile((prevFile) => ({
                                    ...prevFile,
                                    document: {
                                      ...prevFile.document,
                                      tempFields: {
                                        ...prevFile.document.tempFields,
                                        formFields:
                                          prevFile.document.tempFields.formFields.map(
                                            (f) =>
                                              f.name === field.name
                                                ? { ...f, value: isChecked }
                                                : f // Update checkbox value
                                          ),
                                      },
                                    },
                                  }));
                                } else {
                                  handleFieldChange(e, field);
                                }
                              }}
                              onClick={(e) => {
                                e.stopPropagation();

                                if (currCursor === "crosshair") return;

                                setCurrentProcessingFile((prevFile) => {
                                  const isAlreadySelected =
                                    prevFile.document.tempFields.selectedExtraFields.some(
                                      (f) => f.name === field.name
                                    );

                                  return {
                                    ...prevFile,
                                    document: {
                                      ...prevFile.document,
                                      tempFields: {
                                        ...prevFile.document.tempFields,
                                        selectedExtraFields: isAlreadySelected
                                          ? prevFile.document.tempFields
                                              .selectedExtraFields
                                          : e.ctrlKey || e.metaKey
                                            ? [
                                                ...prevFile.document.tempFields
                                                  .selectedExtraFields,
                                                field,
                                              ]
                                            : [field],

                                        selectedFormField: { ...field },
                                      },
                                    },
                                  };
                                });
                              }}
                              style={{
                                position: "absolute",
                                fontSize: `${field.fontSize}px`,
                                left: `${field.x * scaleFactor}px`,
                                top: `${
                                  (currentProcessingFile?.document?.tempFields
                                    ?.currentDocument.actualHeight -
                                    field.y -
                                    field.height) *
                                  scaleFactor
                                }px`,
                                width:
                                  currentProcessingFile?.document?.tempFields
                                    ?.selectedFormField &&
                                  field.name ===
                                    currentProcessingFile?.document?.tempFields
                                      ?.selectedFormField.name
                                    ? `${
                                        currentProcessingFile?.document
                                          ?.tempFields?.selectedFormField
                                          .width * scaleFactor
                                      }px`
                                    : `${field.width * scaleFactor}px`,
                                height:
                                  currentProcessingFile?.document?.tempFields
                                    ?.selectedFormField &&
                                  field.name ===
                                    currentProcessingFile?.document?.tempFields
                                      ?.selectedFormField.name
                                    ? `${
                                        currentProcessingFile?.document
                                          ?.tempFields?.selectedFormField
                                          .height * scaleFactor
                                      }px`
                                    : `${field.height * scaleFactor}px`,
                                border:
                                  typingToolActive ||
                                  fixedInputs.some((prefix) =>
                                    field.name.startsWith(prefix)
                                  )
                                    ? "none"
                                    : "1px solid #000",
                                outline: "none",
                                backgroundColor:
                                  focusedField === field.name
                                    ? "transparent" // Normal background when focused
                                    : highlightFields &&
                                        fixedInputs.some((prefix) =>
                                          field.name.startsWith(prefix)
                                        )
                                      ? "transparent"
                                      : highlightFields
                                        ? "RGBA(255, 211, 0, 0.3)" // Highlighted when not focused
                                        : "transparent",
                                zIndex:
                                  field.type === "PDFCheckBox" ||
                                  field.type === "PDFRadioGroup"
                                    ? 1
                                    : 10,
                                cursor: selectToolActive
                                  ? currCursor
                                  : fixedInputs.some((prefix) =>
                                        field.name.startsWith(prefix)
                                      )
                                    ? "default"
                                    : "text",
                                pointerEvents: isDrawing ? "none" : "auto",
                                ...(field.type === "PDFCheckBox"
                                  ? checkboxStyle(!!field.value)
                                  : {}),
                              }}
                            />

                            {/* Field name overlay when hand tool is active */}
                            {selectToolActive && highlightFields && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  left: `${field.x * scaleFactor}px`,
                                  top: `${
                                    (currentProcessingFile?.document?.tempFields
                                      ?.currentDocument.actualHeight -
                                      field.y -
                                      field.height) *
                                    scaleFactor
                                  }px`,
                                  width: `${field.width * scaleFactor}px`,
                                  height: `${field.height * scaleFactor}px`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  //   color: "#1976d2",
                                  color: "#000",
                                  fontSize: "10px",
                                  fontWeight: "bold",
                                  //   backgroundColor: "rgba(25, 118, 210, 0.1)",
                                  //   border: "1px solid #1976d2",
                                  borderRadius: "2px",
                                  pointerEvents: "none",
                                  zIndex: 15,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  padding: "2px",
                                }}
                              >
                                {field.name}
                              </Box>
                            )}
                          </React.Fragment>
                        );
                      })}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </DndProvider>
  );
};

export default PDFFormEditor;
